import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Lock, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useMoney } from '@/lib/hooks/useMoney'
import { savingsOf } from '@/lib/checkout/annual'
import { intervalLabelKey, offerOptionAt } from '@/components/features/storefront/storefrontDisplay'
import { YearlyPrice } from '@/components/features/storefront/YearlyPrice'
import { useYearlyPriceParts } from '@/components/features/storefront/useYearlyPriceParts'
import { cn } from '@/lib/utils'
import type { PublicProduct, PublicSubscriptionGroup } from '@/types/checkout'
import type { StorefrontAddOn } from '@/types/storefront'

/**
 * The grid rows a card spans when it sits among others (`aligned`): name, price, its second line, the
 * saving, add-on, renewal, benefits, action (David, 2026-10-30: each line on the same line in every card).
 */
export const OFFER_CARD_ROWS = 8

interface OfferPlanCardProps {
  plan: PublicSubscriptionGroup
  /** The options on sale for this offer (a plan reached by one option comes with that one only). */
  options: PublicProduct[]
  /** The billing interval chosen on the page (or inside the card, through `intervalSwitch`). */
  interval: string | null
  intervalSwitch?: ReactNode
  /** The add-on this plan needs to open the space; `null` when the plan opens it by itself. */
  addOn: StorefrontAddOn | null
  /** Another card in the row carries an add-on: this one keeps the row with a neutral line. */
  reserveAddOnRow?: boolean
  /** The plan's lines, shown inside the card when several plans sit side by side. */
  benefits?: string[]
  /** The name on top; off when the headline next to the card already names the plan. */
  showName?: boolean
  /** The community featured one of its options, and there are other plans to compare. */
  recommended?: boolean
  /** Among other cards: price, add-on, benefits and button line up across the row (CSS subgrid). */
  aligned?: boolean
  /** The "Total" line, shown only on a card that carries a required add-on (plan + add-on). */
  showTotal?: boolean
  /** `change`: the person has another plan; the CTA switches to this one. */
  action: 'subscribe' | 'change'
  footer?: ReactNode
  onSubscribe: (option: PublicProduct, addOnIds: string[]) => void
  /** F-195: the plan change page, on this option with the add-on it needs, in one operation. */
  onChangePlan: (option: PublicProduct, addOnIds: string[]) => void
}

/** A plan name this long still fits on the button next to "Assinar". */
const SHORT_NAME = 16


/**
 * A plan on the sale of a padlocked space (David, 2026-10-30: "eu deixaria os
 * planos apresentados igualmente, por mais que tenha uma extensão junto"):
 * the plan's price, the add-on it needs to open the space (part of the same
 * checkout, never a separate card), the total, how it renews, what it
 * includes and "Assinar {plano}". A plan that opens the space by itself
 * offers no add-on here. Side by side, the cards line up row by row.
 */
export function OfferPlanCard({
  plan,
  options,
  interval,
  intervalSwitch,
  addOn,
  reserveAddOnRow = false,
  benefits,
  showName = true,
  recommended = false,
  aligned = false,
  showTotal = true,
  action,
  footer,
  onSubscribe,
  onChangePlan
}: OfferPlanCardProps) {
  const { t } = useTranslation(['common', 'checkout'])
  const formatMoney = useMoney()

  const option = offerOptionAt(options, interval)
  const yearlyOption = option?.billingInterval === 'YEARLY' ? option : null
  const yearlyParts = useYearlyPriceParts({
    option: yearlyOption,
    amount: Number(option?.price ?? 0),
    currency: option?.currency,
    saving: savingsOf(yearlyOption)
  })
  if (!option) return null
  const currency = option.currency
  const addOnAmountRaw = addOn ? option.addOnAmounts?.find((amount) => amount.productId === addOn.id)?.amount : undefined
  const addOnAmount = addOnAmountRaw === undefined ? null : Number(addOnAmountRaw)
  const carried = addOn && addOnAmount !== null ? addOn : null
  const planAmount = Number(option.price)
  const total = planAmount + (carried ? addOnAmount ?? 0 : 0)
  const periodKey = intervalLabelKey(option.billingInterval)
  const period = periodKey ? t(periodKey) : ''
  const commitment = Number(option.commitmentMonths) || 0
  const trialDays = Number(option.trialDays) || 0

  /* The add-on box: the same three lines, size and border on every card, required or not (David, 2026-10-30). */
  const addOnBox = (tone: 'required' | 'none', lines: [ReactNode, ReactNode, ReactNode], testId: string) => (
    <div
      className={cn(
        'flex h-full min-h-[5.75rem] flex-col justify-center gap-0.5 rounded-lg border p-3 text-sm',
        tone === 'required' ? 'border-primary-200 bg-primary-50' : 'border-gray-200 bg-gray-50'
      )}
      data-testid={testId}
      data-add-on={carried?.id}
    >
      <p className="break-words font-medium text-gray-900">{lines[0]}</p>
      <p className="text-gray-700">{lines[1]}</p>
      <p className="flex items-center gap-1 text-xs text-gray-600">{lines[2]}</p>
    </div>
  )
  const addOnRow = carried
    ? addOnBox('required', [
        `+ ${carried.title}`,
        <>{formatMoney(addOnAmount ?? 0, currency)}{period && <span className="text-xs text-gray-500"> {period}</span>}</>,
        <><Lock className="h-3 w-3 flex-shrink-0" aria-hidden="true" />{t('common:access.offer.addOnRequired')}</>
      ], 'space-offer-addon')
    : reserveAddOnRow
      ? addOnBox('none', [
          t('common:access.offer.noAddOnTitle'),
          <span className="text-gray-600">{t('common:access.offer.noAddOnIncluded')}</span>,
          <><Check className="h-3.5 w-3.5 flex-shrink-0 text-primary-600" aria-hidden="true" />{t('common:access.offer.noAddOnUnlocks')}</>
        ], 'space-offer-addon-none')
      : null

  return (
    <div
      className={cn(
        'relative min-w-0 rounded-2xl border bg-white p-6 sm:snap-start',
        aligned ? 'row-span-8 grid grid-rows-subgrid gap-y-0' : 'flex h-full flex-col',
        recommended ? 'border-primary shadow-lg ring-1 ring-primary' : 'border-gray-200 shadow-md'
      )}
      data-testid="space-offer-plan"
      data-plan={plan.slug ?? plan.id}
    >
      {recommended && (
        <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-white">
          {t('common:storefront.recommended')}
        </span>
      )}
      {/* Row 1: the name (or the billing toggle of a single card). */}
      <div>
        {showName && <h3 className="truncate text-xl font-semibold text-gray-900" title={plan.name}>{plan.name}</h3>}
        {intervalSwitch}
      </div>

      {/* Rows 2 to 4: the plan's price, the big number of the card ("12× de R$ X" on a yearly option, as
          the plan's checkout shows it), its second line and the saving, each on its own row. */}
      <div className="mt-4">
        {trialDays > 0 && (
          <p className="mb-2 inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
            {t('common:storefront.trialDays', { count: trialDays })}
          </p>
        )}
        {yearlyOption ? yearlyParts.main : (
          <p className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="text-3xl font-semibold tracking-tight text-gray-900">{formatMoney(planAmount, currency)}</span>
            {period && <span className="text-sm text-gray-500">{period}</span>}
          </p>
        )}
      </div>
      <div>
        {yearlyOption ? yearlyParts.secondary : interval === 'YEARLY' && (
          <p className="mt-1 text-xs text-gray-500">{t('common:storefront.noYearly')}</p>
        )}
      </div>
      <div>{yearlyOption ? yearlyParts.tag : null}</div>

      {/* Row 5: the add-on it needs, or the line saying it needs none. */}
      {aligned ? <div className={cn('h-full', addOnRow && 'pt-4')}>{addOnRow}</div> : addOnRow && <div className="mt-4">{addOnRow}</div>}

      {/* Row 6: how it renews. */}
      <p className="mt-4 flex items-center gap-1.5 text-xs text-gray-500">
        <RefreshCw className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        {commitment > 0 ? t('checkout:contractTerm', { count: commitment }) : t('common:storefront.autoRenews')}
      </p>

      {/* Row 7: what it includes. */}
      <div>
        {benefits && benefits.length === 0 && plan.description && (
          /* No benefit list: the plan's own description, so the card does not read as broken. */
          <p className="mt-4 line-clamp-4 border-t border-gray-100 pt-4 text-sm text-gray-600">{plan.description}</p>
        )}
        {benefits && benefits.length > 0 && (
          <ul className="mt-4 space-y-2 border-t border-gray-100 pt-4 text-sm text-gray-700">
            {benefits.slice(0, 8).map((line, index) => (
              <li key={index} className="flex gap-2">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" aria-hidden="true" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Row 8: the total, quiet, then the one button. */}
      <div className={aligned ? 'mt-4 self-end' : 'mt-auto pt-4'}>
        {/* Only a card that carries a required add-on has a total that differs from its price; the
            others keep the same height blank, so the buttons line up (David, 2026-10-30). */}
        {!(showTotal && carried) && reserveAddOnRow && <div aria-hidden="true" className="mb-3 min-h-[2.5rem]" />}
        {showTotal && carried && (option.billingInterval === 'YEARLY' ? (
          <YearlyPrice option={option} amount={total} currency={currency} addOnIds={carried ? [carried.id] : []} compact className="mb-3" />
        ) : (
          <p className={cn('mb-3 text-sm leading-5 text-gray-600', aligned && 'min-h-[2.5rem]')} data-testid="space-offer-total">
            {t('common:access.offer.totalLine', { total: formatMoney(total, currency), period })}
          </p>
        ))}
        {action === 'change' ? (
          <Button variant="primary" size="lg" className="h-10 w-full" onClick={() => onChangePlan(option, carried ? [carried.id] : [])} data-testid="space-offer-change-plan">
            <span className="truncate">{plan.name.length <= SHORT_NAME ? t('common:access.offer.changeTo', { plan: plan.name }) : t('common:access.offer.change')}</span>
          </Button>
        ) : (
          <Button
            variant="primary"
            size="lg"
            className="h-10 w-full"
            onClick={() => onSubscribe(option, carried ? [carried.id] : [])}
            disabled={!plan.slug}
            data-testid="space-offer-subscribe"
          >
            {/* "Assinar {plano}" when the name fits on the button, else just "Assinar". */}
            <span className="truncate">{plan.name.length <= SHORT_NAME ? t('common:access.offer.subscribeTo', { plan: plan.name }) : t('common:storefront.subscribe')}</span>
          </Button>
        )}
        {footer}
      </div>
    </div>
  )
}
